import { MANAGED_VPATCH_HOLD_NOTE_MAX_LENGTH } from '@guard-dog/contracts';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, type ManagedVpatchItem, type NotificationTestResult } from '../api/client';
import { Async } from '../components/Async';
import { ConfigCheck } from '../components/ConfigCheck';
import { DeliveryHealth } from '../components/DeliveryHealth';
import { Destinations } from '../components/Destinations';
import { ManagedVpatchPublisher } from '../components/ManagedVpatchPublisher';
import { PushToggle } from '../components/PushToggle';
import { useFetch } from '../useFetch';

/**
 * The managed virtual-patch pack and its confidence-soak state (ADR-0011 inc. 2d): whether each
 * patch has graduated monitor→block, or how far into its soak it is. Platform operators can hold a
 * patch back to monitor (the safety valve); everyone else sees the state read-only.
 */

/**
 * Prove the alert channel works — before an incident rather than during one.
 *
 * A configured webhook is not a working webhook, and the difference is invisible until something
 * real fires. Operator-only, because it sends actual outbound traffic on demand.
 */
function AlertChannelTest() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<NotificationTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testKind, setTestKind] = useState<'all' | 'audit'>('all');

  async function send(kind: 'all' | 'audit') {
    setBusy(true);
    setError(null);
    setResult(null);
    setTestKind(kind);
    try {
      setResult(
        kind === 'audit' ? await api.sendAuditAlertTest() : await api.sendTestNotification(),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reach the engine.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3>Alert channel</h3>
      <p className="muted">
        Sends a real notification down the same path a critical finding takes — to your configured
        destinations, or to the engine’s webhook if none match. If it arrives, alerting works.
      </p>
      <button type="button" onClick={() => void send('all')} disabled={busy}>
        {busy ? 'Sending…' : 'Send test notification'}
      </button>
      <button type="button" onClick={() => void send('audit')} disabled={busy}>
        {busy ? 'Sending…' : 'Send audit webhook test'}
      </button>
      {error && <p className="warnbar">{error}</p>}
      {result && !result.configured && (
        // Not an error: this is the answer the operator ran the test to get.
        <p className="warnbar">
          No webhook is configured{result.environment ? ` for ${result.environment}` : ''}, so
          nothing was delivered. The notification was still recorded.
        </p>
      )}
      {result?.configured && result.sent > 0 && (
        <p className="okbar">
          Delivered the {testKind === 'audit' ? 'audit webhook' : 'notification'} test to the{' '}
          {result.environment ?? 'configured'} channel — check it arrived.
        </p>
      )}
      {result?.configured && result.sent === 0 && result.failed > 0 && (
        <p className="warnbar">
          The channel is configured but refused the message.
          {result.lastError ? (
            <>
              {' '}
              It said: <code>{result.lastError}</code>
            </>
          ) : (
            ' The URL is probably wrong or the endpoint is down.'
          )}
        </p>
      )}
      {result?.configured && result.sent === 0 && result.failed === 0 && (
        // Recorded and carried by nothing — which is NOT a refusal. Reporting it as one invented a
        // cause and sent the reader to check a webhook that was working perfectly well. It happens
        // when a routed destination has no transport on this deployment: a browser subscription
        // with no push key, say, which also suppresses the environment-webhook fallback.
        <p className="warnbar">
          The notification was recorded but nothing carried it. A destination is configured that
          this deployment cannot send to
          {result.pushConfigured === false ? ' — push has no key yet' : ''}. Nothing refused it; it
          was never sent.
        </p>
      )}
    </section>
  );
}

export function ManagedVpatches({ isOperator }: { isOperator: boolean }) {
  const state = useFetch(() => api.managedVpatches());
  const [busy, setBusy] = useState<string | null>(null);
  const [status, setStatus] = useState<{
    kind: 'error' | 'success';
    message: string;
  } | null>(null);
  const [holdDraft, setHoldDraft] = useState<{
    id: string;
    label: string;
    note: string;
    mode: 'create' | 'update';
  } | null>(null);
  const holdNoteRef = useRef<HTMLTextAreaElement>(null);
  const returnFocusRef = useRef<HTMLButtonElement | null>(null);
  const holdDraftOpen = holdDraft !== null;

  useEffect(() => {
    if (holdDraftOpen) {
      holdNoteRef.current?.focus();
      return;
    }
    if (busy !== null) return;
    returnFocusRef.current?.focus();
    returnFocusRef.current = null;
  }, [busy, holdDraftOpen]);

  async function releaseHold(vp: ManagedVpatchItem) {
    setStatus(null);
    setBusy(vp.id);
    try {
      await api.setVpatchHold(vp.id, { held: false });
      setStatus({ kind: 'success', message: `Released the hold for ${vp.cve ?? vp.id}.` });
      state.reload();
    } catch {
      setStatus({ kind: 'error', message: `Could not release ${vp.cve ?? vp.id}.` });
    } finally {
      setBusy(null);
    }
  }

  async function saveHoldNote() {
    if (!holdDraft) return;
    const operatorNote = holdDraft.note.trim();
    if (operatorNote === '' || operatorNote.length > MANAGED_VPATCH_HOLD_NOTE_MAX_LENGTH) {
      return;
    }
    setStatus(null);
    setBusy(holdDraft.id);
    try {
      await api.setVpatchHold(holdDraft.id, {
        held: true,
        intent: holdDraft.mode,
        note: operatorNote,
      });
      setStatus({
        kind: 'success',
        message:
          holdDraft.mode === 'create'
            ? `Held ${holdDraft.label} in monitor mode.`
            : `Updated the hold note for ${holdDraft.label}.`,
      });
      setHoldDraft(null);
      state.reload();
    } catch (error) {
      const conflict =
        error instanceof ApiError && error.status === 409 && error.code === 'conflict';
      if (conflict) {
        setHoldDraft((draft) =>
          draft ? { ...draft, mode: draft.mode === 'create' ? 'update' : 'create' } : draft,
        );
        state.reload();
      }
      setStatus({
        kind: 'error',
        message: conflict
          ? `${error.message} The operator note has been preserved; review the refreshed hold state before submitting again.`
          : `Could not ${holdDraft.mode === 'create' ? 'hold' : 'update the hold note for'} ${holdDraft.label}. The operator note has been preserved.`,
      });
    } finally {
      setBusy(null);
    }
  }

  function editHoldNote(
    vp: ManagedVpatchItem,
    mode: 'create' | 'update',
    trigger: HTMLButtonElement,
  ) {
    setStatus(null);
    returnFocusRef.current = trigger;
    setHoldDraft({ id: vp.id, label: vp.cve ?? vp.id, note: '', mode });
  }

  return (
    <section>
      {/* Per-TENANT, so every console user configures their own — this is the surface that
          replaces an operator-only Worker secret (ADR-0020). */}
      <Destinations />
      {/* Delivery health rides directly under the destinations: failures surface where the fix is. */}
      <DeliveryHealth />
      {/* Per-DEVICE, so every console user gets it — not just operators. */}
      <PushToggle />
      {/* Operator-only: it sends real outbound traffic on demand. */}
      {isOperator && <AlertChannelTest />}
      {/* Operator-only: a list of every expected setting is reconnaissance. */}
      {isOperator && <ConfigCheck />}
      <h2>Managed virtual patches</h2>
      {/* Operator-only and monitor-first: the API remains the authoritative publication gate. */}
      {isOperator && <ManagedVpatchPublisher onPublished={state.reload} />}
      <Async state={state}>
        {({ items, policy }) => (
          <>
            <p className="muted">
              Patches ship in monitor mode and promote to <strong>block</strong> automatically after{' '}
              {policy.soakDays} days at ≥{Math.round(policy.minConfidenceToPromote * 100)}%
              confidence — unless an operator holds them.
            </p>
            {status && (
              <p
                className={status.kind === 'error' ? 'error' : 'okbar'}
                role={status.kind === 'error' ? 'alert' : 'status'}
                aria-live={status.kind === 'error' ? 'assertive' : 'polite'}
                aria-atomic="true"
              >
                {status.message}
              </p>
            )}
            {items.length === 0 ? (
              <p className="muted">No managed patches shipped.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>CVE</th>
                    <th>Protects</th>
                    <th>Confidence</th>
                    <th>Soak</th>
                    <th>Enforcing</th>
                    <th>Hold</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((vp) => (
                    <tr key={vp.id}>
                      <td className="mono" title={vp.description}>
                        {vp.cve ?? vp.id}
                      </td>
                      <td>
                        {vp.target ? (
                          <>
                            {vp.target.slug}
                            {vp.target.affectedRange && (
                              <span className="muted"> {vp.target.affectedRange}</span>
                            )}
                          </>
                        ) : (
                          <span className="muted">fleet-wide</span>
                        )}
                      </td>
                      <td>
                        {vp.confidence != null ? `${Math.round(vp.confidence * 100)}%` : '—'}
                        {vp.confidence != null && !vp.soak.confident && (
                          <span className="muted" title="Below the auto-promotion threshold">
                            {' '}
                            (manual)
                          </span>
                        )}
                      </td>
                      <td>{soakCell(vp, policy.soakDays)}</td>
                      <td>
                        <span
                          className={`badge ${vp.action === 'block' ? 'badge-enforce' : 'badge-monitor'}`}
                        >
                          {vp.action === 'block' ? 'block' : 'monitor'}
                        </span>
                      </td>
                      <td>
                        {isOperator ? (
                          <div className="vpatch-hold-controls">
                            <button
                              type="button"
                              className="copy-btn"
                              aria-label={`${vp.soak.held ? 'Release hold for' : 'Hold'} ${vp.cve ?? vp.id}`}
                              disabled={busy !== null || holdDraftOpen}
                              onClick={(event) => {
                                if (vp.soak.held) {
                                  void releaseHold(vp);
                                } else {
                                  editHoldNote(vp, 'create', event.currentTarget);
                                }
                              }}
                            >
                              {vp.soak.held ? 'Release' : 'Hold'}
                            </button>
                            {vp.soak.held && (
                              <button
                                type="button"
                                className="link-button"
                                aria-label={`Update hold note for ${vp.cve ?? vp.id}`}
                                disabled={busy !== null || holdDraftOpen}
                                onClick={(event) => editHoldNote(vp, 'update', event.currentTarget)}
                              >
                                Update note
                              </button>
                            )}
                          </div>
                        ) : vp.soak.held ? (
                          <span className="badge badge-off">held</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {isOperator && holdDraft && (
              <form
                className="form vpatch-hold-form"
                aria-labelledby="managed-vpatch-hold-heading"
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveHoldNote();
                }}
              >
                <strong id="managed-vpatch-hold-heading">
                  {holdDraft.mode === 'create' ? 'Hold' : 'Update the hold note for'}{' '}
                  {holdDraft.label}
                </strong>
                <label htmlFor="managed-vpatch-hold-note">
                  Operator note
                  <textarea
                    ref={holdNoteRef}
                    id="managed-vpatch-hold-note"
                    aria-describedby="managed-vpatch-hold-note-help"
                    rows={3}
                    required
                    maxLength={MANAGED_VPATCH_HOLD_NOTE_MAX_LENGTH}
                    value={holdDraft.note}
                    onChange={(event) =>
                      setHoldDraft((draft) =>
                        draft ? { ...draft, note: event.target.value } : draft,
                      )
                    }
                    placeholder="Why should this patch stay in monitor mode?"
                  />
                </label>
                <span id="managed-vpatch-hold-note-help" className="muted">
                  Required for the operator record. {holdDraft.note.length}/
                  {MANAGED_VPATCH_HOLD_NOTE_MAX_LENGTH} characters. Do not include secrets, personal
                  data, or raw customer indicators. The audit commitment does not hide predictable
                  text.
                </span>
                <div className="vpatch-hold-actions">
                  <button type="submit" disabled={busy !== null || holdDraft.note.trim() === ''}>
                    {busy === holdDraft.id
                      ? 'Saving…'
                      : holdDraft.mode === 'create'
                        ? 'Hold in monitor mode'
                        : 'Update hold note'}
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy !== null}
                    onClick={() => setHoldDraft(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </Async>
    </section>
  );
}

/** Human soak status: held/promoted/countdown/awaiting-clock. */
function soakCell(vp: ManagedVpatchItem, soakDays: number) {
  if (vp.soak.held) return <span className="muted">held to monitor</span>;
  if (vp.soak.promoted) return <span className="muted">promoted</span>;
  if (vp.soak.ageDays === null) return <span className="muted">no clock</span>;
  if (!vp.soak.confident) return <span className="muted">confidence-gated</span>;
  // Eligible and soaking: show the countdown.
  const remaining = Math.max(0, soakDays - vp.soak.ageDays);
  return (
    <span className="muted">
      {vp.soak.ageDays}/{soakDays}d{remaining > 0 ? ` — ${remaining}d left` : ''}
    </span>
  );
}
